import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

import { AlertSoundId } from '@hmax-order/shared/types';

import { ALERT_SOUND_CATALOG } from './alert-sound.catalog';
import { readAlertSoundMuted, storeAlertSoundMuted } from './alert-sound-muted-storage';
import { createAlertAudioContext, playAlertSound } from './alert-sound.player';
import { AlertSoundOutcome } from './alert-sound.type';

/**
 * M2-D68/M2-D69 — quem TOCA os sons de aviso do painel: pedido novo, chamado de garçom e o que vier.
 *
 * `root` de propósito, e é das poucas coisas que podem ser: é do APARELHO, não de uma tela. Um `AudioContext`
 * só para o app inteiro (criar um por som estoura o limite do navegador), e um mudo só para todos os avisos —
 * o tablet da cozinha silencia sem precisar achar um botão por tipo de aviso. Qual som cada tipo toca é da
 * EMPRESA (cfg) e chega de quem chama; este serviço não lê cfg.
 */
@Injectable({ providedIn: 'root' })
export class AlertSoundService {
  private readonly _view = inject(DOCUMENT).defaultView;
  private _context: AudioContext | undefined;

  // Lido na CONSTRUÇÃO da instância, não na avaliação do módulo: o `withState({ muted: readMuted() })` do antigo
  // store de alerta congelava o valor de quem carregou o bundle primeiro (F14/D196).
  private readonly _muted = signal(readAlertSoundMuted());
  private readonly _soundBlocked = signal(false);

  /** Aparelho no mudo: aviso real não toca (a prévia de Configurações toca). */
  readonly muted = this._muted.asReadonly();
  /** O navegador segurou o último som até alguém tocar na página (política de autoplay). */
  readonly soundBlocked = this._soundBlocked.asReadonly();

  toggleMuted(): void {
    const muted = !this._muted();
    storeAlertSoundMuted(muted);
    this._muted.set(muted);
    // Silenciar limpa o aviso de "toque na tela para liberar o som": no mudo ele não diz nada útil, e volta na
    // próxima tentativa real depois de reativar, se o navegador ainda segurar o áudio.
    if (muted) {
      this._soundBlocked.set(false);
    }
  }

  /** Aviso real: respeita o mudo do aparelho. */
  async play(sound: AlertSoundId): Promise<AlertSoundOutcome | 'silent'> {
    if (this._muted()) {
      return 'silent';
    }
    return this.playNow(sound);
  }

  /**
   * "Ouvir" em Configurações: toca mesmo com o aparelho no mudo — é o gerente escolhendo o som, e o toque no
   * botão já é o gesto que o navegador exige para liberar o áudio.
   */
  preview(sound: AlertSoundId): Promise<AlertSoundOutcome> {
    return this.playNow(sound);
  }

  private async playNow(sound: AlertSoundId): Promise<AlertSoundOutcome> {
    this._context ??= this._view ? createAlertAudioContext(this._view) : undefined;
    if (!this._context) {
      return 'unsupported';
    }

    const outcome = await playAlertSound(this._context, ALERT_SOUND_CATALOG[sound] ?? ALERT_SOUND_CATALOG.classic);
    this._soundBlocked.set(outcome === 'blocked');
    return outcome;
  }
}
